import { User } from '../../../common/models/user.model';

/** A row of the users list: the API also sends the account's timestamps (`timestamps: true`). */
export type AdminUser = User & { createdAt?: string; updatedAt?: string };

export type RoleFilter = 'all' | 'staff' | 'vendor';

/** Ardealy staff: anyone holding SUPER_ADMIN. */
export const isStaff = (user: User) => !!user.roles?.includes('SUPER_ADMIN');

/** A vendor's admin: anyone holding VENDOR_ADMIN. */
export const isVendorUser = (user: User) => !!user.roles?.includes('VENDOR_ADMIN');

/**
 * Rows the list never lets you edit, disable or delete: a first role of
 * SUPER_ADMIN (the rule the list has always applied).
 */
export const isLockedRow = (user: User) => user.roles?.[0] === 'SUPER_ADMIN';

/** A user is enabled unless explicitly disabled. */
export const isUserEnabled = (user: User) => user.isEnabled !== false;

export const matchesRole = (user: User, role: RoleFilter) =>
    role === 'all' || (role === 'staff' ? isStaff(user) : isVendorUser(user));

/**
 * The name to show: full name, else whichever half exists, else null (the
 * caller falls back to the email).
 */
export function fullName(user: User): string | null {
    const first = user.firstname?.trim();
    const last = user.lastname?.trim();
    if (first && last) return `${user.firstname} ${user.lastname}`;
    return first ? user.firstname : last ? user.lastname : null;
}

/** Two letters for the avatar. */
export function initials(user: User): string {
    const name = fullName(user);
    if (name) {
        const parts = name.trim().split(/\s+/);
        return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
    }
    return (user.email?.[0] || '?').toUpperCase();
}

const norm = (value?: string | null) => (value ?? '').replace(/ {2,}/g, ' ').toLowerCase();

/**
 * The list's search, matching what it always has: id, first or last name,
 * the full name either way round, email and the raw role. `roleLabel` also
 * lets the words on screen ("Vendor admin") match.
 */
export function matchesSearch(user: User, value: string, roleLabel?: (role: string) => string): boolean {
    const q = value.toLowerCase();
    if (!q) return true;
    return (
        norm(user._id).includes(q) ||
        norm(user.firstname).includes(q) ||
        norm(user.lastname).includes(q) ||
        norm(`${user.lastname} ${user.firstname}`).includes(q) ||
        norm(`${user.firstname} ${user.lastname}`).includes(q) ||
        norm(user.email).includes(q) ||
        (user.roles ?? []).some((role) => role.toLowerCase().includes(q) || (!!roleLabel && roleLabel(role).toLowerCase().includes(q)))
    );
}
